import React from 'react';
import type { Chain, Complex } from '@placer-ui/types';
import { isNil } from 'lodash/fp';
import {
    RecentEntitiesModel,
    RecentEntitiesModelAutocompleteEnriched,
} from 'core/services/recent-entities-service/recent-entities-model';
import { isCustomEntity } from 'core/entities/custom-entity/custom-entity';
import { getReportEntityClosingDate } from 'core/services/report-entities-service/report-entities-service';
import { isRecentEntity } from 'core/entities/recent-entity/recent-entity';
import { BillboardPOI, ChainPOI, MarketPOI, TagPOI, VenuePOI } from 'core/entities';
import { ZipCodeIcon } from '@placer-ui/components';

import { AutoCompleteMenuOptionData } from 'ui-components/auto-complete/auto-complete';
import {
    ChainIcon,
    CustomVenueIcon,
    PinIcon,
    RecentIcon,
    TagIcon,
    VenueIcon,
} from 'components/assets/Icons/Icons';
import { SearchAutocompleteSuggestion } from 'components/search-autocomplete-suggestion/search-autocomplete-suggestion';
import {
    extractPoiRelevantProperties,
    getPoiWarningInfo,
    isAddressProviderType,
} from 'features/header/header-top-panel/search-section/components/search-autocomplete/auto-complete-utils/extract-from-poi-utils';

type AutoCompleteSuggestion = {
    data: RecentEntitiesModelAutocompleteEnriched;
    term: string;
    getIsDisabledOption?: (data: RecentEntitiesModel) => boolean;
    flaggedEntityTooltipText?: string;
    hasUnlockNearbyActivity?: boolean;
    hasNearbyPropertyVenueIndicatorFF?: boolean;
    hasRestrictPermission?: boolean;
    hasStoreIdBadgeAutocompleteSearchPermission?: boolean;
};

export const mapAutoCompleteSuggestion = ({
    data,
    term,
    hasUnlockNearbyActivity,
    hasNearbyPropertyVenueIndicatorFF,
    hasRestrictPermission,
    hasStoreIdBadgeAutocompleteSearchPermission,
}: AutoCompleteSuggestion): AutoCompleteMenuOptionData<RecentEntitiesModel> => {
    const poi = data as Complex;
    const closingDate = getReportEntityClosingDate(poi);

    const {
        isPrivacyFlag,
        isRestricted,
        isNearbyProfile,
        noUnlockNearbyActivity,
        isProviderPresent,
        poiName,
        poiId,
        flaggedTooltipText,
        location,
    } = extractPoiRelevantProperties({
        poi,
        hasUnlockNearbyActivity,
        hasRestrictPermission,
    });

    const hasAnyWarning =
        isPrivacyFlag ||
        hasUnlockNearbyActivity === false ||
        (isNearbyProfile && !!hasNearbyPropertyVenueIndicatorFF) ||
        !!isRestricted;

    const warningInfo = getPoiWarningInfo({
        hasAnyWarning,
        isPrivacyFlag,
        isRestricted,
        isNearbyProfile,
        hasNearbyPropertyVenueIndicatorFF: !!hasNearbyPropertyVenueIndicatorFF,
        noUnlockNearbyActivity,
        flaggedTooltipText,
        closingDate,
    });

    const storeId = hasStoreIdBadgeAutocompleteSearchPermission ? data?.store_id : '';

    const commonOptions = {
        disabled: isPrivacyFlag,
        value: poiName,
        payload: data,
    };

    if (isAddressProviderType(poi)) {
        const exactName = poi.address.formatted_address || poiName;
        return {
            ...commonOptions,
            value: exactName,
            payload: {
                ...data,
                name: exactName,
            },
            component: (
                <SearchAutocompleteSuggestion
                    dataTestId={poiId}
                    key={poiId}
                    name={exactName}
                    termToHighlight={term}
                    Icon={<PinIcon />}
                    warningInfo={warningInfo}
                    rightSideIcon={data.rightSideIcon}
                />
            ),
        };
    }

    if (isRecentEntity(data)) {
        return {
            ...commonOptions,
            component: (
                <SearchAutocompleteSuggestion
                    dataTestId={poiId}
                    key={poiId}
                    Icon={isProviderPresent ? <PinIcon /> : <RecentIcon />}
                    name={poiName}
                    location={location}
                    termToHighlight={term}
                    warningInfo={warningInfo}
                    noUnlockNearbyActivity={!!noUnlockNearbyActivity}
                    storeId={storeId}
                    rightSideIcon={data.rightSideIcon}
                />
            ),
        };
    }

    if (isCustomEntity(data)) {
        return {
            ...commonOptions,
            component: (
                <SearchAutocompleteSuggestion
                    dataTestId={poiId}
                    key={poiId}
                    Icon={isProviderPresent ? <PinIcon /> : <CustomVenueIcon />}
                    name={poiName}
                    location={location}
                    termToHighlight={term}
                    rightSideIcon={data.rightSideIcon}
                />
            ),
        };
    }

    const { purchased, forceLockIconState } = data;
    let isLocked = forceLockIconState === 'show';
    if (isNil(forceLockIconState)) isLocked = !purchased;

    if (ChainPOI.isChain(poi)) {
        const enableLimitedReport = (data as Chain).enable_limited_report;

        return {
            ...commonOptions,
            component: (
                <SearchAutocompleteSuggestion
                    dataTestId={poiId}
                    key={poiId}
                    Icon={<ChainIcon />}
                    name={poiName}
                    isLocked={isLocked && !enableLimitedReport}
                    location={location}
                    termToHighlight={term}
                    rightSideIcon={data.rightSideIcon}
                />
            ),
        };
    }

    if (MarketPOI.isMarketPOI(poi)) {
        return {
            ...commonOptions,
            component: (
                <SearchAutocompleteSuggestion
                    dataTestId={poiId}
                    key={poiId}
                    Icon={<ZipCodeIcon color={'blue'} />}
                    name={poiName}
                    displayUpgradeYourPlan={false}
                    isLocked={isLocked}
                    location={MarketPOI.getLocationLabel(poi)}
                    termToHighlight={term}
                    rightSideIcon={data.rightSideIcon}
                />
            ),
        };
    }

    if (VenuePOI.isVenue(poi)) {
        return {
            ...commonOptions,
            component: (
                <SearchAutocompleteSuggestion
                    dataTestId={poiId}
                    key={poiId}
                    Icon={isProviderPresent ? <PinIcon /> : <VenueIcon />}
                    name={poiName}
                    isLocked={isLocked}
                    location={location}
                    termToHighlight={term}
                    displayUpgradeYourPlan={false}
                    warningInfo={warningInfo}
                    storeId={storeId}
                    noUnlockNearbyActivity={!!noUnlockNearbyActivity || !!isRestricted}
                    rightSideIcon={data.rightSideIcon}
                />
            ),
        };
    }

    if (BillboardPOI.isBillboard(poi)) {
        return {
            ...commonOptions,
            component: (
                <SearchAutocompleteSuggestion
                    dataTestId={poiId}
                    key={poiId}
                    Icon={isProviderPresent ? <PinIcon /> : <VenueIcon />}
                    name={poiName}
                    isLocked={isLocked}
                    location={location}
                    termToHighlight={term}
                    rightSideIcon={data.rightSideIcon}
                />
            ),
        };
    }

    if (TagPOI.isTag(poi)) {
        return {
            ...commonOptions,
            component: (
                <SearchAutocompleteSuggestion
                    dataTestId={poiId}
                    key={poiId}
                    Icon={<TagIcon />}
                    name={poiName}
                    isLocked={isLocked}
                    location={location}
                    termToHighlight={term}
                    rightSideIcon={data.rightSideIcon}
                />
            ),
        };
    }

    return {
        ...commonOptions,
        component: (
            <SearchAutocompleteSuggestion
                dataTestId={poiId}
                key={poiId}
                name={poiName}
                termToHighlight={term}
                Icon={isProviderPresent ? <PinIcon /> : <VenueIcon />}
                rightSideIcon={data.rightSideIcon}
            />
        ),
    };
};
